import React from 'react';
import clsx from 'clsx';

interface ButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  children?: React.ReactNode;
  variant?: 'primary' | 'secondary';
  block?: boolean;
  loading?: boolean;
  icon?: React.ReactNode;
}

/**
 * Primary: ink-1 background, page text, fully rounded pill.
 * Secondary: transparent with ink-1 border.
 */
export const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  block,
  loading,
  icon,
  className,
  disabled,
  ...rest
}) => {
  const isDisabled = !!(disabled || loading);
  return (
    <button
      type="button"
      disabled={isDisabled}
      className={clsx(
        'inline-flex items-center justify-center gap-2 rounded-pill font-sans font-medium text-[14px] transition-opacity duration-150',
        'px-6 py-3.5 leading-none',
        block && 'w-full',
        variant === 'primary' && 'bg-ink1 text-card hover:opacity-90',
        variant === 'secondary' &&
          'bg-card text-ink1 border border-ink1 hover:bg-tint',
        isDisabled && 'opacity-50 cursor-not-allowed',
        className
      )}
      {...rest}
    >
      {loading ? (
        <span
          className={clsx(
            'inline-block h-4 w-4 animate-spin rounded-full border-2 border-t-transparent',
            variant === 'primary' ? 'border-card' : 'border-ink1'
          )}
        />
      ) : icon ? (
        <span className="flex items-center">{icon}</span>
      ) : null}
      {children ? <span>{children}</span> : null}
    </button>
  );
};
